import { apiClient } from "@api/client";
import { ENDPOINT_BASE } from "@api/constants";
import {
  unwrapMarketplaceBody,
  unwrapMarketplaceListBody,
} from "@api/marketplaceResponse";
import { normalizeMarketplaceOrder } from "./marketplaceOrder";
import {
  buildCheckoutOrderPayload,
  buildInterestOrderPayload,
  buildServiceInterestOrderPayload,
  normalizeServiceEoiDetails,
} from "./marketplaceOrderPayload";
import { encodeServiceEoiInterestMessage } from "./serviceEoiMessage";
import {
  milestonesFromOrder,
  toApiMilestonesPayload,
} from "./remanufacturing/remanufacturingMilestoneAdapter";
import {
  catalogMilestoneListsNeedReconcile,
  isCatalogMilestoneKey,
  normalizeCatalogMilestonesList,
  reconcileCatalogMilestoneLists,
  validateCatalogMilestoneForCompletion,
} from "./remanufacturing/catalogMilestones";
import { MILESTONE_STATUS } from "./remanufacturing/constants";
import { recordLocalDashboardDismissal } from "./dashboardDismissals";
import { filterOrdersForDashboard } from "./types";
import { getNormalizedMarketplaceOrderError } from "./marketplaceOrderErrors";

const MARKETPLACE_ORDERS_ENDPOINT =
  ENDPOINT_BASE.MARKETPLACE_ORDERS ?? "/marketplace-orders";

const dashboardMocksEnabled =
  import.meta.env.DEV && import.meta.env.VITE_USE_DASHBOARD_MOCKS === "true";

/**
 * Dev-only hint so MSW can bind the order to the listing owner when product GET
 * omits author Keycloak metadata. Ignored by the real backend.
 * @param {Record<string, unknown>} payload
 * @param {unknown} providerKeycloakId
 */
function appendMockProviderKeycloakHint(payload, providerKeycloakId) {
  if (!dashboardMocksEnabled || providerKeycloakId == null) {
    return payload;
  }

  const normalized = String(providerKeycloakId).trim();
  if (!normalized) {
    return payload;
  }

  return {
    ...payload,
    providerKeycloakId: normalized,
  };
}

export const marketplaceOrdersQueryKey = {
  me: (sub) => ["marketplace-orders", "me", sub ?? "anonymous"],
  received: (sub) => ["marketplace-orders", "received", sub ?? "anonymous"],
  detail: (id) => ["marketplace-orders", "detail", String(id ?? "")],
};

/**
 * Replaces one order row in dashboard list caches (provider + customer views).
 *
 * @param {import("@tanstack/react-query").QueryClient} queryClient
 * @param {string|null|undefined} sub
 * @param {Record<string, unknown>} updatedOrder
 */
export function mergeMarketplaceOrderInListCaches(queryClient, sub, updatedOrder) {
  const id = String(updatedOrder?.id ?? "");
  if (!id) return;

  for (const queryKey of [
    marketplaceOrdersQueryKey.me(sub),
    marketplaceOrdersQueryKey.received(sub),
  ]) {
    queryClient.setQueryData(queryKey, (current) => {
      if (!current || !Array.isArray(current.orders)) return current;
      const index = current.orders.findIndex(
        (order) => String(order?.id) === id
      );
      if (index === -1) return current;
      const orders = [...current.orders];
      orders[index] = updatedOrder;
      return { ...current, orders };
    });
  }
}

/**
 * @param {unknown} items
 * @param {Record<string, unknown>|null} [pagination]
 */
/**
 * @param {unknown} items
 * @param {Record<string, unknown>|null} [pagination]
 * @param {import("./types").MarketplaceOrderStatusPerspective} [perspective]
 */
function normalizeOrderCollection(items, pagination = null, perspective) {
  let rawOrders = [];

  if (Array.isArray(items)) {
    rawOrders = items;
  } else if (items && typeof items === "object") {
    const record = /** @type {Record<string, unknown>} */ (items);
    if (Array.isArray(record.content)) {
      rawOrders = record.content;
    } else if (Array.isArray(record.orders)) {
      rawOrders = record.orders;
    }
  }

  const orders = filterOrdersForDashboard(
    rawOrders.map((row) => normalizeMarketplaceOrder(row)).filter(Boolean),
    perspective
  );

  const total = orders.length;

  return {
    orders,
    total,
    pagination: pagination ?? null,
  };
}

/**
 * @param {{ signal?: AbortSignal, page?: number, size?: number, status?: string }} [options]
 */
export async function getMyMarketplaceOrders({
  signal,
  page = 0,
  size = 100,
  status,
} = {}) {
  const params = { page, size };
  if (status) {
    params.status = status;
  }

  const response = await apiClient.get(`${MARKETPLACE_ORDERS_ENDPOINT}/me`, {
    signal,
    params,
  });
  const { items, pagination } = unwrapMarketplaceListBody(response.data, {
    endpoint: `${MARKETPLACE_ORDERS_ENDPOINT}/me`,
  });
  return normalizeOrderCollection(items, pagination, "customer");
}

/**
 * @param {{ signal?: AbortSignal, page?: number, size?: number, status?: string }} [options]
 */
export async function getReceivedMarketplaceOrders({
  signal,
  page = 0,
  size = 100,
  status,
} = {}) {
  const params = { page, size };
  if (status) {
    params.status = status;
  }

  const response = await apiClient.get(
    `${MARKETPLACE_ORDERS_ENDPOINT}/received`,
    {
      signal,
      params,
    }
  );
  const { items, pagination } = unwrapMarketplaceListBody(response.data, {
    endpoint: `${MARKETPLACE_ORDERS_ENDPOINT}/received`,
  });
  return normalizeOrderCollection(items, pagination, "provider");
}

/**
 * Backend returns **501 NOT IMPLEMENTED** — do not use in dashboards.
 * Prefer the selected row from `GET .../me` or `GET .../received`.
 */
export async function getMarketplaceOrderById(orderId, { signal } = {}) {
  const response = await apiClient.get(
    `${MARKETPLACE_ORDERS_ENDPOINT}/${orderId}`,
    {
      signal,
    }
  );
  const data = unwrapMarketplaceBody(response.data, {
    endpoint: `${MARKETPLACE_ORDERS_ENDPOINT}/${orderId}`,
  });
  return normalizeMarketplaceOrder(data) ?? data;
}

export async function createMarketplaceOrder(data) {
  const payload = appendMockProviderKeycloakHint(
    buildCheckoutOrderPayload(data),
    data?.providerKeycloakId
  );
  const response = await apiClient.post(MARKETPLACE_ORDERS_ENDPOINT, payload);
  const created = unwrapMarketplaceBody(response.data, {
    endpoint: MARKETPLACE_ORDERS_ENDPOINT,
  });
  return normalizeMarketplaceOrder(created) ?? created;
}

/**
 * Express interest — `POST /marketplace-orders` with `source: "interest"`.
 * @param {{
 *   productId: string|number,
 *   message?: string,
 *   eoiDetails?: Record<string, unknown>|null,
 *   providerKeycloakId?: string,
 * }} data
 */
export async function createProductInterest({
  productId,
  message,
  eoiDetails,
  providerKeycloakId,
}) {
  const normalizedEoi = normalizeServiceEoiDetails(eoiDetails);
  const payload = appendMockProviderKeycloakHint(
    normalizedEoi != null
      ? buildServiceInterestOrderPayload({
          productId,
          message: encodeServiceEoiInterestMessage({
            message,
            eoiDetails: normalizedEoi,
          }),
          eoiDetails: normalizedEoi,
          includeServiceWorkflowField: true,
        })
      : buildInterestOrderPayload({ productId, message }),
    providerKeycloakId
  );

  const response = await apiClient.post(MARKETPLACE_ORDERS_ENDPOINT, payload);
  const created = unwrapMarketplaceBody(response.data, {
    endpoint: MARKETPLACE_ORDERS_ENDPOINT,
  });
  return normalizeMarketplaceOrder(created) ?? created;
}

/**
 * @param {string} orderId
 * @param {{ verdict: "will_quote"|"declined", note?: string }} data
 */
export async function submitServiceOrderFeasibility(orderId, data) {
  const response = await apiClient.patch(
    `${MARKETPLACE_ORDERS_ENDPOINT}/${orderId}/feasibility`,
    data
  );
  const updated = unwrapMarketplaceBody(response.data, {
    endpoint: `${MARKETPLACE_ORDERS_ENDPOINT}/${orderId}/feasibility`,
  });
  return normalizeMarketplaceOrder(updated) ?? updated;
}

/**
 * @param {string} orderId
 * @param {{
 *   amount?: number,
 *   currency?: string,
 *   deliveryDate?: string,
 *   expiresAt?: string,
 *   note?: string,
 * }} data
 */
export async function submitServiceOrderQuote(orderId, data) {
  const response = await apiClient.patch(
    `${MARKETPLACE_ORDERS_ENDPOINT}/${orderId}/quote`,
    data
  );
  const updated = unwrapMarketplaceBody(response.data, {
    endpoint: `${MARKETPLACE_ORDERS_ENDPOINT}/${orderId}/quote`,
  });
  return normalizeMarketplaceOrder(updated) ?? updated;
}

/**
 * @param {string} orderId
 * @param {{ poReference?: string }} [data]
 */
export async function acceptServiceOrderQuote(orderId, data = {}) {
  const response = await apiClient.patch(
    `${MARKETPLACE_ORDERS_ENDPOINT}/${orderId}/quote/accept`,
    data
  );
  const updated = unwrapMarketplaceBody(response.data, {
    endpoint: `${MARKETPLACE_ORDERS_ENDPOINT}/${orderId}/quote/accept`,
  });
  return normalizeMarketplaceOrder(updated) ?? updated;
}

export async function acceptMarketplaceOrder(orderId, data = {}) {
  const response = await apiClient.patch(
    `${MARKETPLACE_ORDERS_ENDPOINT}/${orderId}/accept`,
    data
  );
  const accepted = unwrapMarketplaceBody(response.data, {
    endpoint: `${MARKETPLACE_ORDERS_ENDPOINT}/${orderId}/accept`,
  });
  return normalizeMarketplaceOrder(accepted) ?? accepted;
}

export async function declineMarketplaceOrder(orderId, data = {}) {
  const response = await apiClient.patch(
    `${MARKETPLACE_ORDERS_ENDPOINT}/${orderId}/decline`,
    data
  );
  const declined = unwrapMarketplaceBody(response.data, {
    endpoint: `${MARKETPLACE_ORDERS_ENDPOINT}/${orderId}/decline`,
  });
  return normalizeMarketplaceOrder(declined) ?? declined;
}

/**
 * Customer cancels an express-of-interest (`source: "interest"`).
 * Allowed when `status` is `pending_provider` or `active`.
 *
 * @param {string} orderId
 * @param {{ note?: string }} [data]
 */
export async function cancelMarketplaceOrder(orderId, data = {}) {
  const response = await apiClient.patch(
    `${MARKETPLACE_ORDERS_ENDPOINT}/${orderId}/cancel`,
    data
  );
  const cancelled = unwrapMarketplaceBody(response.data, {
    endpoint: `${MARKETPLACE_ORDERS_ENDPOINT}/${orderId}/cancel`,
  });
  return normalizeMarketplaceOrder(cancelled) ?? cancelled;
}

/**
 * Removes a cancelled express-of-interest from the caller's My Requests dashboard.
 * Falls back to a local dismissal record when the backend endpoint is unavailable.
 *
 * @param {string} orderId
 * @param {{ sub?: string|null, perspective: "customer"|"provider" }} context
 */
export async function dismissCancelledMarketplaceOrder(orderId, context) {
  const { sub, perspective } = context;

  try {
    const response = await apiClient.patch(
      `${MARKETPLACE_ORDERS_ENDPOINT}/${orderId}/dismiss`
    );
    const dismissed = unwrapMarketplaceBody(response.data, {
      endpoint: `${MARKETPLACE_ORDERS_ENDPOINT}/${orderId}/dismiss`,
    });
    return normalizeMarketplaceOrder(dismissed) ?? dismissed;
  } catch (error) {
    const normalized = getNormalizedMarketplaceOrderError(error);
    const status = normalized?.status ?? normalized?.httpStatus;

    if (status === 404 || status === 501 || status === 405) {
      recordLocalDashboardDismissal(sub, perspective, orderId);
      return null;
    }

    throw error;
  }
}

export async function advanceMarketplaceOrderFulfillment(orderId) {
  const response = await apiClient.patch(
    `${MARKETPLACE_ORDERS_ENDPOINT}/${orderId}/fulfillment/advance`
  );
  const advanced = unwrapMarketplaceBody(response.data, {
    endpoint: `${MARKETPLACE_ORDERS_ENDPOINT}/${orderId}/fulfillment/advance`,
  });
  return normalizeMarketplaceOrder(advanced) ?? advanced;
}

function isStaleCatalogMilestoneApiError(error) {
  const normalized = getNormalizedMarketplaceOrderError(error);
  if (normalized?.code === "CATALOG_MILESTONE_INCOMPLETE") {
    return true;
  }
  const message = String(normalized?.message ?? "");
  return /activities must be complete/i.test(message);
}

function passesLocalCatalogMilestoneCompletion(milestones) {
  const normalized = normalizeCatalogMilestonesList(milestones);
  if (!normalized.some((milestone) => isCatalogMilestoneKey(milestone.key))) {
    return false;
  }

  return normalized
    .filter((milestone) => milestone.status === MILESTONE_STATUS.COMPLETED)
    .every((milestone) => validateCatalogMilestoneForCompletion(milestone).ok);
}

function mergeCatalogMilestonesIntoOrder(existingOrder, milestones) {
  const clientMilestones = normalizeCatalogMilestonesList(milestones);
  return (
    normalizeMarketplaceOrder({
      ...existingOrder,
      serviceMilestones: { milestones: clientMilestones },
    }) ?? {
      ...existingOrder,
      serviceMilestones: { milestones: clientMilestones },
    }
  );
}

/**
 * Staging/production backends may return milestone shells without activity progress.
 * Prefer the validated client snapshot when the API response is incomplete.
 *
 * @param {Record<string, unknown>|null|undefined} order
 * @param {import("./remanufacturing/remanufacturingMilestones").ServiceMilestone[]} sentMilestones
 */
function reconcileOrderCatalogMilestones(order, sentMilestones) {
  const sent = normalizeCatalogMilestonesList(sentMilestones);
  if (!order || !sent.some((milestone) => isCatalogMilestoneKey(milestone.key))) {
    return order;
  }

  const persisted = milestonesFromOrder(order);
  if (!catalogMilestoneListsNeedReconcile(persisted, sent)) {
    return order;
  }

  const mergedList = reconcileCatalogMilestoneLists(persisted, sent);

  return (
    normalizeMarketplaceOrder({
      ...order,
      serviceMilestones: { milestones: mergedList },
    }) ?? order
  );
}

function shouldApplyDevCatalogMilestoneFallback(milestones) {
  const normalized = normalizeCatalogMilestonesList(milestones);
  if (!normalized.some((milestone) => isCatalogMilestoneKey(milestone.key))) {
    return false;
  }

  const completing = normalized.filter(
    (milestone) => milestone.status === MILESTONE_STATUS.COMPLETED
  );
  if (!completing.length) {
    return false;
  }

  return completing.every(
    (milestone) => validateCatalogMilestoneForCompletion(milestone).ok
  );
}

export async function updateMarketplaceOrderMilestones(
  orderId,
  milestones,
  existingOrder = null
) {
  try {
    const response = await apiClient.put(
      `${MARKETPLACE_ORDERS_ENDPOINT}/${orderId}/milestones`,
      {
        milestones: toApiMilestonesPayload(milestones),
      }
    );
    const updated = unwrapMarketplaceBody(response.data, {
      endpoint: `${MARKETPLACE_ORDERS_ENDPOINT}/${orderId}/milestones`,
    });
    const normalized =
      normalizeMarketplaceOrder(updated) ?? updated;
    return reconcileOrderCatalogMilestones(normalized, milestones);
  } catch (error) {
    if (
      import.meta.env.DEV &&
      existingOrder &&
      shouldApplyDevCatalogMilestoneFallback(milestones) &&
      passesLocalCatalogMilestoneCompletion(milestones)
    ) {
      console.warn(
        "[dev] Catalog milestone PUT rejected or incomplete on API. Applying validated milestone state locally."
      );
      return reconcileOrderCatalogMilestones(
        mergeCatalogMilestonesIntoOrder(existingOrder, milestones),
        milestones
      );
    }
    throw error;
  }
}
